import React, { useEffect, useState, useCallback } from 'react';
import {
  ArrowDownTrayIcon, EnvelopeIcon,
  ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../lib/apiClient';

interface Enquiry {
  id: string;
  name: string;
  email: string;
  company: string | null;
  role: string | null;
  message: string;
  estimated_mrr: number | null;
  status: 'new' | 'read' | 'replied' | 'converted';
  created_at: string;
}

interface Stats { total: number; new: number; replied: number; converted: number }
interface Pagination { total: number; current_page: number; per_page: number; last_page: number }

const STATUS_COLORS: Record<string, string> = {
  new:       'bg-emerald-500 text-white',
  read:      'bg-slate-400 text-white',
  replied:   'bg-brand-500 text-white',
  converted: 'bg-purple-500 text-white',
};

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
}
function getAvatarBg(email: string) {
  const p = ['bg-blue-100 text-blue-700', 'bg-purple-100 text-purple-700', 'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-700'];
  return p[email.charCodeAt(0) % p.length];
}

export default function SalesEnquiries() {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [updating, setUpdating] = useState<string | null>(null);

  const fetchEnquiries = useCallback(() => {
    setLoading(true);
    const params: Record<string, any> = { page };
    if (statusFilter) params.status = statusFilter;
    if (search) params.search = search;
    apiClient.get('/admin/enquiries', { params })
      .then(({ data }) => {
        setEnquiries(data.data?.data ?? data.data ?? []);
        setStats(data.stats);
        const pg = data.data;
        setPagination({ total: pg.total, current_page: pg.current_page, per_page: pg.per_page, last_page: pg.last_page });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page, statusFilter, search]);

  useEffect(() => { fetchEnquiries(); }, [fetchEnquiries]);

  const markStatus = async (id: string, status: string) => {
    setUpdating(id);
    await apiClient.patch(`/admin/enquiries/${id}`, { status })
      .then(() => fetchEnquiries())
      .finally(() => setUpdating(null));
  };

  return (
    <div className="px-6 py-6 max-w-[1400px] mx-auto flex flex-col gap-6 pb-20">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-[28px] font-extrabold text-slate-900 tracking-tight">Sales & Enquiries</h1>
          <p className="text-[13px] text-slate-500 mt-1">Manage inbound interest and website form submissions.</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-[13px] font-bold hover:bg-slate-50 transition-colors shadow-sm">
            <ArrowDownTrayIcon className="w-4 h-4" /> Export
          </button>
          <button className="flex items-center gap-2 bg-brand-500 text-white px-4 py-2.5 rounded-xl text-[13px] font-bold hover:bg-brand-600 transition-colors shadow-lg shadow-brand-500/20">
            <EnvelopeIcon className="w-4 h-4" /> Broadcast Email
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Total Enquiries', value: stats?.total,    color: 'text-slate-900' },
          { label: 'New',             value: stats?.new,      color: 'text-emerald-600' },
          { label: 'Replied',         value: stats?.replied,  color: 'text-brand-600' },
          { label: 'Converted',       value: stats?.converted,color: 'text-purple-600' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">{s.label}</p>
            {loading ? <div className="h-8 w-16 bg-slate-100 animate-pulse rounded" /> :
              <p className={`text-[32px] font-extrabold leading-none ${s.color}`}>{s.value ?? 0}</p>}
          </div>
        ))}
      </div>

      {/* Filters + Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-1">
        <div className="flex items-center gap-1">
          {[{ label: 'All Submissions', value: '' }, { label: 'New', value: 'new' }, { label: 'Replied', value: 'replied' }, { label: 'Converted', value: 'converted' }].map(tab => (
            <button key={tab.value}
              onClick={() => { setStatusFilter(tab.value); setPage(1); }}
              className={`px-4 py-2.5 text-[13px] font-bold transition-colors rounded-t-lg ${
                statusFilter === tab.value
                  ? 'text-brand-600 border-b-2 border-brand-600 bg-brand-50/40'
                  : 'text-slate-500 hover:text-slate-800'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 pb-2">
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" placeholder="Search enquiries..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 pr-3 py-2 bg-white border border-slate-200 text-[13px] rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 w-48" />
          </div>
          <div className="flex items-center gap-2 text-[13px] font-bold text-slate-700">
            <div className="w-2.5 h-2.5 rounded-full bg-brand-500" />
            {loading ? '—' : `${stats?.new ?? 0} New Enquiries`}
          </div>
          <div className="flex items-center gap-2 text-[13px] font-bold text-slate-400">
            <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
            {loading ? '—' : `${pagination?.total ?? 0} Total`}
          </div>
        </div>
      </div>

      {/* Enquiries List */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-4 bg-slate-50/50 border-b border-slate-100 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
          <div className="col-span-4">Sender & Company</div>
          <div className="col-span-4">Inquiry Message</div>
          <div className="col-span-2">Submitted</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        <div className="flex flex-col divide-y divide-slate-100">
          {loading ? (
            Array.from({length:4}).map((_,i) => (
              <div key={i} className="grid grid-cols-12 items-center px-6 py-6 gap-4">
                <div className="col-span-4 flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-100 animate-pulse shrink-0" />
                  <div className="space-y-2 flex-1"><div className="h-4 bg-slate-100 animate-pulse rounded w-3/4" /><div className="h-3 bg-slate-50 animate-pulse rounded w-full" /></div>
                </div>
                <div className="col-span-4 space-y-2"><div className="h-4 bg-slate-100 animate-pulse rounded" /><div className="h-3 bg-slate-50 animate-pulse rounded w-5/6" /></div>
                <div className="col-span-2"><div className="h-4 bg-slate-100 animate-pulse rounded" /></div>
                <div className="col-span-2" />
              </div>
            ))
          ) : enquiries.length === 0 ? (
            <div className="px-6 py-12 text-center text-slate-400 font-medium">No enquiries found.</div>
          ) : enquiries.map(e => (
            <div key={e.id} className="grid grid-cols-12 items-start px-6 py-6 hover:bg-slate-50/50 transition-colors">
              <div className="col-span-4 flex items-center gap-3 pr-4">
                <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-[14px] font-black ${getAvatarBg(e.email)}`}>
                  {getInitials(e.name)}
                </div>
                <div>
                  <p className="text-[14px] font-bold text-slate-900 leading-tight">{e.name}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{e.email}</p>
                  {e.company && <p className="text-[10px] font-extrabold text-brand-600 uppercase tracking-widest mt-1">{e.company}</p>}
                </div>
              </div>
              <div className="col-span-4 pr-6">
                {e.role && <p className="text-[13px] font-bold text-slate-900 mb-1">{e.role}</p>}
                <p className="text-[12px] text-slate-500 leading-snug line-clamp-3">{e.message}</p>
                {e.estimated_mrr && (
                  <p className="text-[11px] font-extrabold text-emerald-600 mt-1.5">
                    Est. ${Number(e.estimated_mrr).toLocaleString()} MRR
                  </p>
                )}
              </div>
              <div className="col-span-2">
                <p className="text-[12px] font-bold text-slate-900">{new Date(e.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{new Date(e.created_at).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})}</p>
                <span className={`inline-block mt-1.5 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_COLORS[e.status]}`}>{e.status}</span>
              </div>
              <div className="col-span-2 text-right flex flex-col gap-1.5 items-end">
                {e.status === 'new' && (
                  <button onClick={() => markStatus(e.id, 'read')} disabled={updating === e.id}
                    className="text-[11px] font-bold text-brand-600 hover:text-slate-700 disabled:opacity-40">
                    Mark Read
                  </button>
                )}
                {e.status !== 'converted' && (
                  <button onClick={() => markStatus(e.id, 'replied')} disabled={updating === e.id}
                    className="text-[11px] font-bold text-emerald-600 hover:text-emerald-800 disabled:opacity-40">
                    Mark Replied
                  </button>
                )}
                <button onClick={() => markStatus(e.id, 'converted')} disabled={updating === e.id || e.status === 'converted'}
                  className="text-[11px] font-bold text-purple-600 hover:text-purple-800 disabled:opacity-30">
                  Convert
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/30">
            <span className="text-[13px] text-slate-500">
              Showing {(pagination.current_page-1)*pagination.per_page+1}–{Math.min(pagination.current_page*pagination.per_page, pagination.total)} of {pagination.total}
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={pagination.current_page===1}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 disabled:opacity-30">
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(p => Math.min(pagination.last_page,p+1))} disabled={pagination.current_page===pagination.last_page}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 disabled:opacity-30">
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
